import {Button, Col, Form, Modal, Row, Spinner} from 'react-bootstrap'
import {IMembersNode, IResAddAssignmentGroup} from '../../../types/assignmentGroups'
import {useMutation, useQuery} from '@apollo/client'
import {GET_ALL_USERS_LIST} from '../../../gql/queries/usersQuery'
import {useEffect, useMemo} from 'react'
import {toast} from 'react-toastify'
import {IResAllListUsers} from '../../../types/users'
import {parseId} from '../../../helpers'
import {GET_ALL_ORGANIZATIONS} from '../../../gql/queries/organizationsQuery'
import {useGetUser} from '../../../store/userStore'
import {MultiSelectWithActions} from '../../../components/MultiSelectWithActions'

import * as Yup from 'yup'
import {useFormik} from 'formik'
import {CREATE_GROUP_ASSIGNMENT, UPDATE_GROUP_ASSIGNMENT} from '../../../../modules/assignmentGroupMutation'

interface IModalGroups {
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  infoGroup?: null | {
    id: string
    name: string
    members: IMembersNode[]
    supervisorIds?: (string | number)[]
    organizationId?: string | number
    visibleForTickets?: boolean
  }
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required('El nombre es requerido'),
  supervisors: Yup.array().of(Yup.string()),
})

export const ModalAddAssignmentGroup: React.FC<IModalGroups> = ({
  isOpen,
  onClose,
  infoGroup,
  refetch,
}) => {
  const normalizeId = (id?: string | number | null) => (id ? `${parseId(id)}` : '')

  // Get current user's role and organization
  const userGroups = useGetUser((s) => s.user?.groups) || []
  const currentUser = useGetUser((s) => s.user?.user)
  const isSuperAdministrador = userGroups.includes('SuperAdministrador')

  const mutationDoc = infoGroup ? UPDATE_GROUP_ASSIGNMENT : CREATE_GROUP_ASSIGNMENT
  const [gqlCreate, {data: dataCreate, loading: loadingCreate, error: errorCreate}] =
    useMutation<IResAddAssignmentGroup>(mutationDoc)

  const {data: dataUsers, loading: loadingUsers, error} = useQuery<IResAllListUsers>(
    GET_ALL_USERS_LIST,
    {fetchPolicy: 'cache-first'}
  )

  // Query for organizations
  const {
    data: dataOrganizations,
    loading: loadingOrganizations,
    error: errorOrganizations,
  } = useQuery(GET_ALL_ORGANIZATIONS, {
    variables: {first: 100},
    fetchPolicy: 'no-cache',
  })

  // Filtrar supervisores del listado de usuarios
  const supervisors = useMemo(() => {
    if (!dataUsers?.allUsersList) return []
    return dataUsers.allUsersList.filter((user) => user.primaryGroup?.name === 'Supervisor')
  }, [dataUsers])

  const supervisorOptions = useMemo(
    () =>
      supervisors.map((supervisor) => ({
        value: String(parseId(supervisor.id)),
        label: `${supervisor.firstName} ${supervisor.lastName} - ${supervisor.email}`,
      })),
    [supervisors]
  )

  const memberOptions = useMemo(
    () =>
      (dataUsers?.allUsersList || []).map((user) => {
        const groups =
          user.groupAssignmentSet?.edges?.map(({node}) => node.name).filter(Boolean) || []
        const groupSuffix = groups.length ? ` | Groups: ${groups.join(', ')}` : ''
        return {
          value: String(parseId(user.id)),
          label: `${user.firstName} ${user.lastName} - ${user.email}${groupSuffix}`,
        }
      }),
    [dataUsers]
  )

  const formik = useFormik({
    initialValues: {
      name: '',
      user: [] as string[],
      supervisors: [] as string[],
      organizationId: '' as string | number,
      visibleForTickets: false,
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const payload = {
        name: values.name,
        userIds: values.user.map((id) => parseId(id)),
        supervisorIds: values.supervisors.map(id => parseId(id)),
        organizationId: values.organizationId ? parseId(values.organizationId) : undefined,
        visibleForTickets: values.visibleForTickets,
      }

      if (infoGroup) {
        gqlCreate({
          variables: {
            groupId: parseId(infoGroup.id),
            groupData: payload,
          },
        })
      } else {
        gqlCreate({
          variables: {
            groupData: payload,
          },
        })
      }
    },
  })

  const selectedSupervisorOptions = useMemo(() => {
    const selectedIds = (formik.values.supervisors || []).map((id) => String(parseId(id)))
    return supervisorOptions.filter((opt) => selectedIds.includes(String(opt.value)))
  }, [formik.values.supervisors, supervisorOptions])

  const selectedMemberOptions = useMemo(() => {
    const selectedIds = new Set((formik.values.user || []).map((id) => String(parseId(id))))
    return memberOptions.filter((opt) => selectedIds.has(String(opt.value)))
  }, [formik.values.user, memberOptions])

  const memberSelectStyles = useMemo(
    () => ({
      control: (base: any) => ({
        ...base,
        minHeight: '42px',
        alignItems: 'flex-start',
      }),
      valueContainer: (base: any) => ({
        ...base,
        maxHeight: '110px',
        overflowY: 'auto',
      }),
    }),
    []
  )

  useEffect(() => {
    if (!infoGroup) {
      // Set organization from current user if not SuperAdministrador
      if (!isSuperAdministrador && currentUser?.organization?.id) {
        formik.setFieldValue('organizationId', normalizeId(currentUser.organization.id))
      }
      return
    }
    formik.setValues({
      name: infoGroup.name,
      user: infoGroup.members.map(({node}) => String(parseId(node.id))),
      supervisors: infoGroup.supervisorIds?.map((id) => String(parseId(id))) || [],
      organizationId: infoGroup.organizationId || (currentUser?.organization?.id ? normalizeId(currentUser.organization.id) : ''),
      visibleForTickets: Boolean(infoGroup.visibleForTickets),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [infoGroup])

  useEffect(() => {
    if (error) toast.error('Error: ' + error.message)
  }, [error])

  useEffect(() => {
    if (errorOrganizations) toast.error('Error loading organizations: ' + errorOrganizations.message)
  }, [errorOrganizations])

  useEffect(() => {
    if (!dataCreate) return
    const payload = dataCreate.createGroupAssignmentMutation || dataCreate.updateGroupAssignmentMutation
    if (payload?.groupAssignment?.id) {
      toast.success(infoGroup ? 'Assignment group updated successfully' : 'Assignment group created successfully')
      refetch()
      onClose()
    } else if (payload?.errors?.length) {
      toast.error(
        `Error ${
          payload.errors[0]?.message || 'Something happened'
        }`
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataCreate])

  useEffect(() => {
    if (errorCreate) toast.error('Error: ' + errorCreate.message)
  }, [errorCreate])

  return (
    <Modal show={isOpen} onHide={onClose} size='xl' centered>
      <Modal.Header closeButton>
        <Modal.Title>{infoGroup ? `Edit ${infoGroup.name}` : 'Add Assignment Group'}</Modal.Title>
      </Modal.Header>
      {loadingUsers || !dataUsers ? (
        <>
          <Modal.Body style={{minHeight: '56vh'}}>
            <Form.Group className='mb-3' controlId='name'>
              <Form.Label>Name</Form.Label>
              <Form.Control placeholder='Enter Group name' disabled />
            </Form.Group>
            <Form.Group className='mb-3' controlId='supervisors'>
              <Form.Label>Supervisors</Form.Label>
              <div style={{padding: '10px', border: '1px solid #dbdfe9', borderRadius: '4px', backgroundColor: '#f5f5f5'}}>
                <Spinner animation='border' size='sm' /> Loading supervisors...
              </div>
            </Form.Group>
            <Form.Group className='mb-3' controlId='organizationId'>
              <Form.Label>Organization</Form.Label>
              <Form.Select disabled>
                <option value=''>Loading...</option>
              </Form.Select>
            </Form.Group>
            <div className='mt-8'>
              <h6 className='mb-4'>Members</h6>
              <div
                className='w-100 d-flex align-items-center justify-content-center'
                style={{height: '70px'}}
              >
                <Spinner animation='border' />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button disabled variant='secondary'>
              Close
            </Button>
            <Button disabled variant='primary'>
              Save Changes
            </Button>
          </Modal.Footer>
        </>
      ) : (
        <Form onSubmit={formik.handleSubmit}>
          <Modal.Body style={{minHeight: '56vh'}}>
            <Row className='g-3 align-items-end'>
              <Col md={5}>
                <Form.Group controlId='name'>
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    placeholder='Enter Group name'
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    isInvalid={Boolean(formik.touched.name && formik.errors.name)}
                  />
                  <Form.Control.Feedback type='invalid'>{formik.errors.name}</Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group controlId='organizationId'>
                  <Form.Label>Organization</Form.Label>
                  <Form.Select
                    name='organizationId'
                    value={formik.values.organizationId}
                    onChange={formik.handleChange}
                    isInvalid={Boolean(formik.errors.organizationId && formik.touched.organizationId)}
                    disabled={!isSuperAdministrador || loadingOrganizations}
                  >
                    {loadingOrganizations ? (
                      <option value=''>Loading...</option>
                    ) : (
                      <option value=''>Select One</option>
                    )}
                    {dataOrganizations?.allOrganizations?.edges?.map(({node}: any) => {
                      const value = normalizeId(node.id)
                      return (
                        <option key={node.id} value={value}>
                          {node.name}
                        </option>
                      )
                    })}
                  </Form.Select>
                  <Form.Control.Feedback type='invalid'>
                    {formik.errors.organizationId}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group controlId='visibleForTickets' className='mb-1'>
                  <Form.Check
                    type='checkbox'
                    name='visibleForTickets'
                    label='Visible for tickets'
                    checked={Boolean(formik.values.visibleForTickets)}
                    onChange={formik.handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className='g-3 mt-1'>
              <Col md={4}>
                <Form.Group controlId='supervisors'>
                  <Form.Label>Supervisors (optional)</Form.Label>
                  <MultiSelectWithActions
                    value={selectedSupervisorOptions}
                    options={supervisorOptions}
                    onChange={(selected) => {
                      const values = Array.isArray(selected)
                        ? selected.map((opt: any) => String(opt?.value || '').trim()).filter(Boolean)
                        : []
                      formik.setFieldValue('supervisors', values)
                    }}
                  />
                  {supervisors.length === 0 && (
                    <Form.Text className='text-muted'>
                      No supervisors available. Only users with "Supervisor" role will appear here.
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>

              <Col md={8}>
                <Form.Group controlId='members'>
                  <Form.Label>Members</Form.Label>
                  <MultiSelectWithActions
                    value={selectedMemberOptions}
                    options={memberOptions}
                    styles={memberSelectStyles}
                    onChange={(selected) => {
                      const values = Array.isArray(selected)
                        ? selected.map((opt: any) => String(opt?.value || '').trim()).filter(Boolean)
                        : []
                      formik.setFieldValue('user', values)
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button type='button' variant='secondary' onClick={onClose}>
              Close
            </Button>
            <Button
              type='submit'
              variant='primary'
              disabled={loadingCreate}
              className='d-flex align-items-center gap-2'
            >
              {loadingCreate && <Spinner size='sm' animation='border' />}
              Save Changes
            </Button>
          </Modal.Footer>
        </Form>
      )}
    </Modal>
  )
}
